import { useCallback, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { useInView } from 'react-intersection-observer'
import { useTranslation } from './locale'
import { useRouting } from './routes'
import { usePropertyCardTracking } from '../components/region-landing-page/hooks'
import usePriceFormatter from './price'
import { ListingSectionObject } from '../transports/common/listing-section-transport'
import { Features, FeaturesFormatter } from '../models/common/features-model'
import { BreadcrumbListTransport } from '../models/common/breadcrumb-model'
import { Offer } from '../models/common/offers-model'
import { FormattedSuitableFeatures, SuitablesModel } from '../models/common/suitables-model'
import { getPriorityOfferId, getPropertyClickRedirectUrl } from '../components/region-landing-page/utils'
import { CategorizedTraumTags, getTraumTagsData } from '../utils/traum-tag-utils'
import { getTruncatedTextByChars } from '../utils/miscellaneous-utils'
import { encodeToBase64 } from '../utils/encoding-utils'
import { isServer } from '../utils/platform-utils'
import { scheduleIdleCallback } from '../utils/browser-utils'
import { SharedRotatingIndex } from '../utils/shared-rotating-index'
import AnalyticsService from '../services/analytics-service'
import get from 'lodash/get'
import isEmpty from 'lodash/isEmpty'
import ImageTransport from '../transports/common/image-transport'
import ListingStatsTransport from '../transports/common/listing-stats-transport'
import AdvancedBreadcrumbTransport from '../transports/common/advanced-breadcrumb-transport'
import { formatClassification } from '../utils/classification-utils'
import { FormattedClassificationTransport } from '../transports/common/listing-transport'
import { useSrlTracking } from './use-srl-tracking'
import { pageTypeEventCategories } from '../services/analytics-service/categories'
import { getSearchRequestId } from '../redux/filters/selectors'
import { Image } from '../components/property-details/expose-gallery/gallery/types'

const PAID_TRAUM_TAG_ROTATION_INTERVAL = 8000
const FEATURES_LIMIT = 5
const sharedRotatingIndex = new SharedRotatingIndex(PAID_TRAUM_TAG_ROTATION_INTERVAL)

interface UseCardDataProps {
    index: number
    listingObject: ListingSectionObject
    requestUrl?: string
    pageName: string
    sectionName: string
    displayPrice?: boolean
    displayPriceInfo?: boolean
    showCheckbox?: boolean
    showMaxPersonCount?: boolean
    isSelected?: boolean
    onToggleSelect?: (id: number) => void
    // For object-overview-card compatibility
    showDescription?: boolean
    showRegion?: boolean
    showCtaButton?: boolean
    isObjectOverview?: boolean
    propagateSearchRequestId?: boolean
    breadcrumbModel: BreadcrumbListTransport
    offerModel: Offer
    suitablesModel: SuitablesModel
}

interface UseCardDataReturn {
    // Basic data
    listingId: number
    propertyTitle: string
    propertyDescriptionText: string
    regionName: string
    regionLink: string
    propertyImage: ImageTransport
    partnerName: string | null

    // Price and offer data
    priceValue: string
    priceBeforeOffer: string | null
    priceInBaseUnits?: number
    priceCurrency?: string
    priceInfoValue: string | null
    formattedOffer: any
    isFreeCancellation: boolean

    // Rating data
    shouldShowRating: boolean
    formattedRatingAverage: string
    formattedRatingCount: string

    // Feature and tag data
    hasFeatures: boolean
    computedFeatures: Features | null
    traumTagsData: CategorizedTraumTags
    currentBadgeTraumTag: any

    // Status flags
    isTopObject: boolean
    isPremiumTopObject: boolean
    showSuperfastResponderTag: boolean
    isDirectBooking: boolean

    // Display control props
    displayPrice: boolean
    displayPriceInfo: boolean
    showCheckbox: boolean
    isSelected: boolean
    showDescription: boolean
    showRegion: boolean
    showCtaButton: boolean
    isObjectOverview: boolean

    // Tracking
    intersectionTargetRef: (node?: Element | null) => void

    // Event handlers
    handleCardClick: () => void
    handleCheckboxChange: (e: React.ChangeEvent<HTMLInputElement>) => void
    handleCtaClick: (e: React.MouseEvent) => void

    // Additional data for object-overview-card
    description: string
    truncatedDescription: string
    stats: ListingStatsTransport<number>
    breadcrumbs: AdvancedBreadcrumbTransport[]
    breadcrumbList?: BreadcrumbListTransport
    href: string
    seoHref?: string
    noCrawl: boolean
    suitablesData: FormattedSuitableFeatures | null
    cardImages: ImageTransport[]
    virtualTourVideo?: Image
    classification: FormattedClassificationTransport | null
}

export const useCardData = ({
    index,
    listingObject,
    requestUrl,
    pageName,
    sectionName,
    displayPrice = true,
    displayPriceInfo = true,
    showCheckbox = false,
    showMaxPersonCount = false,
    isSelected = false,
    onToggleSelect,
    showDescription = true,
    showRegion = true,
    showCtaButton = false,
    isObjectOverview = false,
    propagateSearchRequestId = false,
    breadcrumbModel,
    offerModel,
    suitablesModel,
}: UseCardDataProps): UseCardDataReturn => {
    const { t } = useTranslation()
    const { r } = useRouting()
    const formatPrice = usePriceFormatter()
    const searchRequestId = useSelector(getSearchRequestId)

    const siteRelativeURL = isServer ? '/' : window.location.pathname + window.location.search
    const priorityOfferId = getPriorityOfferId(siteRelativeURL, r)

    // Extract basic properties
    const listingId = get(listingObject, 'id')
    const rating = get(listingObject, 'rating', null)
    const breadcrumbs = get(listingObject, 'breadcrumbs', [])
    const stats = get(listingObject, 'stats', {})
    const isPremiumTopObject = get(listingObject, 'isPremiumTopObject', false)
    const isTopObject = get(listingObject, 'isTopObject', false)
    const images = get(listingObject, 'images', [])
    const videoTransport = get(listingObject, 'video', null)
    const href = get(listingObject, 'href', '')
    const seoHref = get(listingObject, 'seoHref')
    const noCrawl = get(listingObject, 'noCrawl', false)
    const offer = get(listingObject, 'offer', null)
    const price = get(listingObject, 'price', {})
    const isFreeCancellation = get(listingObject, 'isFreeCancellation', false)
    const showSuperfastResponderTag = get(listingObject, 'showSuperfastResponderTag', false)
    const isDirectBooking = Number(get(listingObject, 'bookingType')) === 2
    const seoRegion = get(listingObject, 'seoRegion', {})
    const description = get(listingObject, 'shortDescription', '')

    const propertyType = get(listingObject, 'propertyType', '')
    const title = get(listingObject, 'title', '')
    const partnerName = get(listingObject, 'externalPartnerInfo.name', null)
    const classification = useMemo(() => formatClassification(listingObject, t), [listingObject, t])

    // Property title (grid-card logic)
    const propertyTitle = `${propertyType} ${title}`.trim()

    // Offer formatting
    const formattedOffer = useMemo(() => {
        return offer ? offerModel.format(offer, priorityOfferId) : null
    }, [offer, offerModel, priorityOfferId])

    // Breadcrumb formatting
    const formattedBreadcrumb = useMemo(() => {
        return breadcrumbModel?.getLastRegionLink(breadcrumbs, '') || null
    }, [breadcrumbModel, breadcrumbs])

    const regionName = get(formattedBreadcrumb, 'title') || get(seoRegion, 'name', '')
    const regionLink = get(formattedBreadcrumb, 'url') || get(seoRegion, 'url.url', '')

    // Property tracking (grid-card approach)
    const srl = useSrlTracking(listingObject, index)
    const property = usePropertyCardTracking(listingObject)

    const trackingRef =
        pageName === pageTypeEventCategories.SEARCH_RESULT_LISTING_PAGE
            ? srl.intersectionRef
            : property.intersectionTargetRef

    const propertyCardClickTracking =
        pageName === pageTypeEventCategories.SEARCH_RESULT_LISTING_PAGE
            ? srl.handleSrlClick
            : property.propertyCardClickTracking

    const [rotationRef, isVisible] = useInView({
        initialInView: false,
        threshold: 0.2,
        triggerOnce: false,
    })

    const intersectionTargetRef = useCallback(
        (node?: Element | null) => {
            trackingRef(node)
            rotationRef(node)
        },
        [trackingRef, rotationRef],
    )

    // Property image
    const propertyImage = get(images, '[0]', null)

    // For object-overview-card compatibility - use apartment images if available
    const apartmentImages = get(listingObject, 'apartment.images', [])
    const cardImages = (apartmentImages && apartmentImages.length > 0 ? apartmentImages : images) || []

    // Property description (grid-card logic: bedrooms + bathrooms only)
    const propertyDescriptionText = useMemo(() => {
        const bedroomsCount = get(stats, 'bedrooms', null)
        const bathroomCount = get(stats, 'bathrooms', null)
        const maxPersonsCount = get(stats, 'maxPersons', null)

        const parts: string[] = []

        if (bedroomsCount) {
            parts.push(t('properBedRoomCount', { count: bedroomsCount }))
        }

        if (showMaxPersonCount && maxPersonsCount) {
            parts.push(t('maxGuestCount', { count: maxPersonsCount }))
        }

        if (bathroomCount) {
            parts.push(t('bathroomCount', { count: bathroomCount }))
        }

        return parts.join('• ')
    }, [stats, t, showMaxPersonCount])

    // Price info
    const externalPartnerInfo = get(listingObject, 'externalPartnerInfo', null)
    const isPriceFormatted = get(price, 'isPriceFormatted', false)
    const notFormattedPrice = get(price, 'notFormattedPrice', null)

    const priceValue = useMemo(() => {
        if (isPriceFormatted) {
            return get(price, 'price', '')
        }

        return formatPrice(notFormattedPrice)
    }, [price, isPriceFormatted, notFormattedPrice, formatPrice])

    const priceBeforeOffer = get(price, 'priceBeforeOffer', '')

    const priceInfoValue = useMemo(() => {
        const persons = get(price, 'persons')
        const unit = get(price, 'unit')

        if (!persons || !unit) {
            return ''
        }

        if (isPriceFormatted) {
            return `${persons} / ${unit}`
        }

        const nightsText = t('nights', { count: Number(unit) || 0 })
        const guestsText = t('guests', { count: Number(persons) || 0 })
        return `${persons} ${guestsText} / ${unit} ${nightsText}`
    }, [price, t, isPriceFormatted])

    // Rating validation
    const shouldShowRating = useMemo(() => {
        return Boolean(
            rating &&
                get(rating, 'count', 0) > 0 &&
                get(rating, 'average') !== undefined &&
                get(rating, 'average') !== null,
        )
    }, [rating])

    const formattedRatingAverage = useMemo(() => Number(rating?.average).toFixed(1), [rating])
    const formattedRatingCount = useMemo(() => `(${rating?.count})`, [rating])

    // Features
    const computedFeatures = useMemo(() => {
        const { generalEquipments, suitables, stats } = listingObject
        const suitablesMap = suitables ? suitablesModel.format(suitables) : {}
        return new FeaturesFormatter(t).format(
            generalEquipments || null,
            null,
            suitablesMap,
            {},
            {},
            0,
            0,
            stats?.maxPersons || 0,
            stats?.apartmentSize || 0,
            0,
            FEATURES_LIMIT,
            false,
            false,
        )
    }, [listingObject, t, suitablesModel])

    const hasFeatures = !isEmpty(computedFeatures)

    // Traum tags
    const traumTagIds = get(listingObject, 'traumTagIds', [])

    const traumTagsData = getTraumTagsData(traumTagIds)

    const [rotatingIndex, setRotatingIndex] = useState(() => sharedRotatingIndex.getCurrentIndex())

    // Subscribe to the shared rotating index (single interval for all cards)
    // This prevents memory spikes from having many individual intervals
    useEffect(() => {
        // Only subscribe if visible and has multiple badge tags to rotate
        if (!isVisible || traumTagsData.badgeTraumTags.length <= 1) {
            return
        }

        const unsubscribe = sharedRotatingIndex.subscribe((index: number) => {
            setRotatingIndex(index)
        })

        return unsubscribe
    }, [isVisible, traumTagsData.badgeTraumTags.length])

    const currentBadgeTraumTag = useMemo(() => {
        const badgeTags = traumTagsData.badgeTraumTags
        if (badgeTags.length === 0) {
            return
        }
        return badgeTags[rotatingIndex % badgeTags.length]
    }, [traumTagsData.badgeTraumTags, rotatingIndex])

    // Description truncation for object-overview-card
    const truncatedDescription = useMemo(() => {
        return getTruncatedTextByChars(description, 200)
    }, [description])

    // Suitables data (pet-friendly, smoking policy, etc.)
    const suitablesData = useMemo(() => {
        const suitables = get(listingObject, 'suitables', [])
        if (!suitables || suitables.length === 0) {
            return null
        }
        return suitablesModel.formatedSuitableFeatures(suitables)
    }, [listingObject, suitablesModel])

    // Event handlers (grid-card tracking approach)
    const traumBaseUrl = useMemo(() => t('traumBaseUrl'), [t])

    const handleCardClick = useCallback(() => {
        const safeHref = typeof href === 'string' ? href : ''
        const eventCategory = `card-${pageName}-${sectionName}`
        const _objectURL = getPropertyClickRedirectUrl(traumBaseUrl, safeHref)

        // Handle traumBoost tracking
        if (isPremiumTopObject || isTopObject) {
            _objectURL.searchParams.set(
                'from_page',
                isServer ? encodeToBase64(requestUrl ?? '') : encodeToBase64(window.location.href),
            )
            _objectURL.searchParams.set('tb', '1')
        }

        if (propagateSearchRequestId && searchRequestId) {
            _objectURL.searchParams.set('searchRequestId', searchRequestId)
        }

        window.open(_objectURL.href, '_blank')

        scheduleIdleCallback(() => {
            propertyCardClickTracking(listingObject)
        })

        AnalyticsService.trackClickEvent(eventCategory, _objectURL.toString())
    }, [
        href,
        traumBaseUrl,
        listingObject,
        propertyCardClickTracking,
        isPremiumTopObject,
        isTopObject,
        requestUrl,
        pageName,
        sectionName,
        searchRequestId,
        propagateSearchRequestId,
    ])

    const handleCheckboxChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            e.stopPropagation()
            if (onToggleSelect) {
                onToggleSelect(listingId)
            }
        },
        [listingId, onToggleSelect],
    )

    const handleCtaClick = useCallback(
        (e: React.MouseEvent) => {
            e.stopPropagation()
            const redirectUrl = getPropertyClickRedirectUrl(traumBaseUrl, href)

            if (propagateSearchRequestId && searchRequestId) {
                redirectUrl.searchParams.set('searchRequestId', searchRequestId)
            }

            const eventCategory = `cta-${pageName}-${sectionName}-${listingId}`
            AnalyticsService.trackClickEvent(eventCategory, redirectUrl.toString())

            window.open(redirectUrl.toString(), '_blank')
        },
        [href, listingId, traumBaseUrl, pageName, sectionName, searchRequestId, propagateSearchRequestId],
    )

    return {
        // Basic data
        listingId,
        propertyTitle,
        propertyDescriptionText,
        regionName,
        regionLink,
        propertyImage,
        partnerName,

        // Price and offer data
        priceValue,
        priceBeforeOffer,
        priceInBaseUnits: get(price, 'priceInBaseUnits'),
        priceCurrency: get(price, 'currency'),
        priceInfoValue,
        formattedOffer,
        isFreeCancellation,

        // Rating data
        shouldShowRating,
        formattedRatingAverage,
        formattedRatingCount,

        // Feature and tag data
        hasFeatures,
        computedFeatures,
        traumTagsData,
        currentBadgeTraumTag,

        // Status flags
        isTopObject,
        isPremiumTopObject,
        showSuperfastResponderTag,
        isDirectBooking,

        // Display control props
        displayPrice,
        displayPriceInfo,
        showCheckbox,
        isSelected,
        showDescription,
        showRegion,
        showCtaButton,
        isObjectOverview,

        // Tracking
        intersectionTargetRef,

        // Event handlers
        handleCardClick,
        handleCheckboxChange,
        handleCtaClick,

        // Additional data for object-overview-card
        description,
        truncatedDescription,
        stats,
        breadcrumbs,
        href,
        seoHref,
        noCrawl,
        cardImages,
        virtualTourVideo: !!videoTransport
            ? {
                  src: videoTransport.url,
                  thumbnail: videoTransport.thumbnailUrl,
                  caption: '',
              }
            : undefined,
        suitablesData,
        classification,
    }
}
